/**
 * VideoHold — the picture WAITS for the sound.
 *
 * A re-voiced line that is longer than its place used to be cut at the next
 * line, and the words were lost (RU, 30 days to 14.10.2026: 22 of 291 finished
 * /redub sessions had at least one line cut, median 0.4 s of speech, p90 2.2 s,
 * max 6.3 s; 9 of the 22 downloaded the result with the words missing). When
 * the person lets the recording make room, the SOUND track is opened at that
 * point (RedubAssembly.timeShift) — and a video has to follow it: at the same
 * moment the frame holds for as long as the line needs, and everything after
 * plays that much later. A line added after the last frame holds the last frame.
 *
 * The file is built by the engine of the editing timeline (/video-editor):
 * VideoMergeWebCodecs.merge — pieces of the source video and still frames, the
 * whole new soundtrack as one free audio item. It encodes on the video card;
 * a file without holds keeps the stream-copy export of the page and never
 * comes here.
 *
 * window.VideoHold:
 *   plan(videoSec, cuts, endSec) → [{ src: [a, b] } | { hold: sec, at: videoSec }]
 *       cuts  — [{ at, add }] in VIDEO seconds, any order;
 *       endSec — where the sound ends on the output; past the picture the last
 *                frame holds. Pure arithmetic, no browser needed.
 *   render(file, { cuts, lead, soundSec, audio, slug, signal, onProgress })
 *       → Promise<{ blob, outFormat, holds, heldSec, ms }>
 *       cuts — [{ at, add }] in SOUND seconds (the voice track's own time);
 *       lead — where the sound starts in the video (MP4TrackRemux.soundStart);
 *       soundSec — length of `audio` (the whole new track, holds included).
 *       Throws an Error with `kind: 'hold-unavailable'` and `reason` when this
 *       browser or this container cannot be rebuilt — the caller says so and
 *       offers the sound file; nothing is cut silently.
 */
(function (root) {
    'use strict';

    var MIN_PIECE = 1 / 120;      // a piece shorter than a frame at 120 fps is not a piece
    var MIN_HOLD = 0.02;

    function plan(videoSec, cuts, endSec) {
        var dur = Math.max(0, +videoSec || 0);
        var cs = (cuts || []).map(function (c) { return { at: Math.max(0, Math.min(dur, +c.at || 0)), add: Math.max(0, +c.add || 0) }; })
            .filter(function (c) { return c.add >= MIN_HOLD; })
            .sort(function (a, b) { return a.at - b.at; });
        var out = [], prev = 0, held = 0;
        function hold(at, sec) {
            var last = out[out.length - 1];
            if (last && last.hold != null && Math.abs(last.at - at) < 1e-3) last.hold += sec;
            else out.push({ hold: sec, at: at });
            held += sec;
        }
        for (var i = 0; i < cs.length; i++) {
            if (cs[i].at - prev >= MIN_PIECE) out.push({ src: [prev, cs[i].at] });
            hold(cs[i].at, cs[i].add);
            prev = Math.max(prev, cs[i].at);
        }
        if (dur - prev >= MIN_PIECE) out.push({ src: [prev, dur] });
        var tail = (+endSec || 0) - (dur + held);
        if (tail >= MIN_HOLD) hold(dur, tail);
        return out;
    }

    function even(n) { n = Math.max(2, Math.round(n)); return n - (n % 2); }

    function videoElement(file) {
        return new Promise(function (resolve, reject) {
            var v = document.createElement('video');
            v.muted = true; v.playsInline = true; v.preload = 'auto';
            var url = URL.createObjectURL(file), done = false;
            function fin(ok, x) { if (done) return; done = true; if (!ok) { try { URL.revokeObjectURL(url); } catch (_) {} } (ok ? resolve : reject)(x); }
            v.addEventListener('loadedmetadata', function () {
                var d = v.duration;
                if (!(d > 0) || !isFinite(d)) {
                    var lw = root.__mediaLength;
                    if (typeof lw === 'function') { lw(v).then(function (L) { v.__len = L; fin(true, { v: v, url: url }); }, function () { fin(true, { v: v, url: url }); }); return; }
                }
                fin(true, { v: v, url: url });
            }, { once: true });
            v.addEventListener('error', function () { fin(false, Object.assign(new Error('video unreadable'), { kind: 'hold-unavailable', reason: 'video-unreadable' })); }, { once: true });
            setTimeout(function () { fin(false, Object.assign(new Error('video metadata timeout'), { kind: 'hold-unavailable', reason: 'metadata-timeout' })); }, 20000);
            v.src = url;
        });
    }

    // Frames per second of the source, by the median sample duration (the engine's own rule).
    async function sourceFps(file, VM) {
        try {
            var d = (VM.isMatroska && VM.isMatroska(file) && VM.demuxMatroskaLazy) ? await VM.demuxMatroskaLazy(file) : await VM.demuxLazy(file);
            var s = (d && d.samples) || [], durs = [];
            for (var i = 0; i < s.length && i < 90; i++) if (s[i].dur > 0) durs.push(s[i].dur);
            if (durs.length) { durs.sort(function (a, b) { return a - b; }); var md = durs[durs.length >> 1]; if (md > 0) return 1e6 / md; }
        } catch (_) {}
        return 30;
    }

    // The engine of the editing timeline is ~250 KB: loaded when a file with holds is
    // asked for, not on every visit to a page that may never need it.
    var _engine = null;
    function loadScript(path, global) {
        if (root[global]) return Promise.resolve();
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = (typeof root.__jsSrc === 'function') ? root.__jsSrc(path) : path;
            s.async = true;
            var n = document.querySelector('script[nonce]');
            if (n && n.nonce) s.setAttribute('nonce', n.nonce);
            s.onload = function () { root[global] ? resolve() : reject(new Error(global + ' missing after load')); };
            s.onerror = function () { reject(new Error('failed to load ' + path)); };
            document.head.appendChild(s);
        });
    }
    function ensureEngine() {
        if (root.VideoMergeWebCodecs && root.CanvasVideoExport) return Promise.resolve(root.VideoMergeWebCodecs);
        if (!_engine) {
            _engine = loadScript('/js/canvas-video-export.js', 'CanvasVideoExport')
                .then(function () { return loadScript('/js/video-merge-webcodecs.js', 'VideoMergeWebCodecs'); })
                .then(function () { return root.VideoMergeWebCodecs; }, function (e) { _engine = null; throw e; });
        }
        return _engine;
    }

    async function render(file, o) {
        o = o || {};
        var VM = null, t0 = Date.now();
        var why = '';
        try { VM = await ensureEngine(); } catch (e) { VM = null; why = String((e && e.message) || e || '').slice(0, 80); }
        if (!VM || typeof VM.merge !== 'function') throw Object.assign(new Error('engine missing'), { kind: 'hold-unavailable', reason: 'engine-missing' + (why ? ': ' + why : '') });
        var lead = +o.lead || 0;
        var el = await videoElement(file);
        var v = el.v;
        try {
            var videoSec = (v.duration > 0 && isFinite(v.duration)) ? v.duration : (+v.__len || 0);
            if (!(videoSec > 0) || !(v.videoWidth > 0)) throw Object.assign(new Error('no picture'), { kind: 'hold-unavailable', reason: 'no-picture' });
            var cuts = (o.cuts || []).map(function (c) { return { at: (+c.at || 0) + lead, add: +c.add || 0 }; });
            var soundSec = +o.soundSec || 0;
            var pieces = plan(videoSec, cuts, soundSec + Math.max(0, lead));
            var fps = Math.min(60, Math.max(1, Math.round(await sourceFps(file, VM))));
            var target = { tw: even(v.videoWidth), th: even(v.videoHeight), fps: fps };
            var srcSec = videoSec;
            // the frame a hold shows: the one that comes next, so the picture resumes without a jump
            async function frameAt(sec) {
                var tt = Math.max(0, Math.min(srcSec - 1 / (fps * 2), sec));
                if (typeof root.__seekPresented === 'function') await root.__seekPresented(v, tt, 8000);
                else await new Promise(function (r) { v.addEventListener('seeked', r, { once: true }); v.currentTime = tt; });
                var c = document.createElement('canvas'); c.width = target.tw; c.height = target.th;
                c.getContext('2d').drawImage(v, 0, 0, target.tw, target.th);
                return (typeof createImageBitmap === 'function') ? createImageBitmap(c) : c;
            }
            var mkv = !!(VM.isMatroska && VM.isMatroska(file));
            var clips = pieces.map(function (p) {
                if (p.src) {
                    return { file: file, duration: srcSec, width: target.tw, height: target.th, fps: fps, hasAudio: false,
                        trimStart: p.src[0], trimEnd: p.src[1], speed: 1, rotation: 0, volume: 100, mute: true, audioFadeIn: 0, audioFadeOut: 0 };
                }
                return { kind: 'image', duration: p.hold, trimStart: 0, trimEnd: p.hold, speed: 1, hasAudio: false,
                    width: target.tw, height: target.th, fps: 0,
                    image: function () { return frameAt(p.at); },
                    releaseImage: function (b) { if (b && b.close) b.close(); } };
            });
            var transitions = [];
            for (var i = 0; i < clips.length - 1; i++) transitions.push({ type: 'none', duration: 0 });
            var held = pieces.reduce(function (s, p) { return s + (p.hold || 0); }, 0);
            var outSec = srcSec + held;
            // The sound sits where the movie plays it: `lead` into the picture (a track
            // starting before the picture — negative lead — loses its head, as in the source).
            var aStart = Math.max(0, lead), aTrim = Math.max(0, -lead);
            var aLen = Math.max(0.01, Math.min(soundSec - aTrim, outSec - aStart));
            var opts = { format: 'mp4', crf: 18, matroska: mkv, slug: o.slug || 'video-hold', target: target, fit: 'contain',
                // the source's own density, so a picture re-encoded around the holds keeps its quality
                bitrate: (file.size > 0 && srcSec > 0) ? Math.max(1.5e6, Math.min(40e6, Math.round(file.size * 8 / srcSec))) : undefined,
                audioItems: [{ file: o.audio, start: aStart, len: aLen, trimStart: aTrim, trimEnd: aTrim + aLen, loop: false }] };
            var pr = await VM.probe(clips, opts);
            if (!pr || !pr.mode) throw Object.assign(new Error('engine refused: ' + ((pr && pr.reason) || '?')), { kind: 'hold-unavailable', reason: String((pr && pr.reason) || 'probe') });
            opts.outFormat = pr.outFormat || 'mp4';
            var blob = await VM.merge(clips, transitions, pr.target, opts, { signal: o.signal, onProgress: o.onProgress || function () {} });
            return { blob: blob, outFormat: opts.outFormat, holds: pieces.filter(function (p) { return p.hold != null; }).length,
                heldSec: Math.round(held * 100) / 100, ms: Date.now() - t0 };
        } finally {
            try { v.removeAttribute('src'); v.load(); } catch (_) {}
            try { URL.revokeObjectURL(el.url); } catch (_) {}
        }
    }

    root.VideoHold = { plan: plan, render: render };
})(typeof window !== 'undefined' ? window : globalThis);
